import type { NostrEvent, NostrFilter, NRelay } from "@nostrify/nostrify";
import { nip19 } from "nostr-tools";
import { getKindInfo, listKnownKinds, storageClass } from "./nostrKinds";

/** OpenAI-compatible tool/function definition. */
export interface ToolDef {
  type: "function";
  function: {
    name: string;
    description: string;
    parameters: Record<string, unknown>;
  };
}

/** The result returned to the model after a tool runs. */
export interface ToolResult {
  /** Text fed back to the model. */
  content: string;
  /** Nostr events surfaced to the UI for rich rendering. */
  events?: NostrEvent[];
  /** An event template the user is being asked to sign/publish. */
  draft?: {
    kind: number;
    content: string;
    tags: string[][];
  };
}

export interface ToolContext {
  nostr: NRelay;
  userPubkey?: string;
  signal?: AbortSignal;
}

/** All tool schemas the model is offered. */
export const TOOL_DEFS: ToolDef[] = [
  {
    type: "function",
    function: {
      name: "lookup_kind",
      description:
        "Look up what a Nostr event kind means: its name, purpose, storage class, defining NIP, and rendering hints. Use this whenever the user mentions a kind number or you encounter an unfamiliar kind.",
      parameters: {
        type: "object",
        properties: {
          kind: { type: "number", description: "The numeric event kind to describe." },
        },
        required: ["kind"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_kinds",
      description:
        "List the event kinds known to this browser, with short descriptions. Use to orient yourself or answer 'what can I look at' style questions.",
      parameters: { type: "object", properties: {} },
    },
  },
  {
    type: "function",
    function: {
      name: "decode_nip19",
      description:
        "Decode a NIP-19 identifier (npub, nprofile, note, nevent, naddr) into its raw parts (hex pubkey, event id, kind, d-tag, relays). Always decode before querying.",
      parameters: {
        type: "object",
        properties: {
          identifier: { type: "string", description: "A bech32 nostr identifier, e.g. npub1… or naddr1…" },
        },
        required: ["identifier"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "query_events",
      description:
        "Query relays for events matching a Nostr filter. Returns matching events which will be rendered for the user. Combine kinds and use tag filters when possible. Keep limit modest (default 20).",
      parameters: {
        type: "object",
        properties: {
          kinds: { type: "array", items: { type: "number" }, description: "Event kinds to fetch." },
          authors: { type: "array", items: { type: "string" }, description: "Hex pubkeys of authors." },
          ids: { type: "array", items: { type: "string" }, description: "Hex event ids." },
          e_tags: { type: "array", items: { type: "string" }, description: "Referenced event ids (the #e filter)." },
          p_tags: { type: "array", items: { type: "string" }, description: "Referenced pubkeys (the #p filter)." },
          t_tags: { type: "array", items: { type: "string" }, description: "Hashtags / topics (the #t filter)." },
          d_tags: { type: "array", items: { type: "string" }, description: "Addressable identifiers (the #d filter)." },
          search: { type: "string", description: "Free-text search (NIP-50, relay support varies)." },
          since: { type: "number", description: "Unix timestamp lower bound." },
          until: { type: "number", description: "Unix timestamp upper bound." },
          limit: { type: "number", description: "Max events (default 20, hard cap 100)." },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "fetch_event",
      description:
        "Fetch a single event by its NIP-19 identifier (note, nevent, naddr) or by hex id. Returns the event for rendering.",
      parameters: {
        type: "object",
        properties: {
          identifier: { type: "string", description: "A note1/nevent1/naddr1 identifier, or a 64-char hex event id." },
        },
        required: ["identifier"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "fetch_profile",
      description:
        "Fetch the profile metadata (kind 0) for a user, by npub/nprofile or hex pubkey. Returns the profile event for rendering.",
      parameters: {
        type: "object",
        properties: {
          identifier: { type: "string", description: "An npub1/nprofile1 identifier, or a 64-char hex pubkey." },
        },
        required: ["identifier"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "whoami",
      description:
        "Get the currently logged-in user's hex pubkey and npub, or report that nobody is logged in. Use before signing or when the user refers to 'me', 'my', etc.",
      parameters: { type: "object", properties: {} },
    },
  },
  {
    type: "function",
    function: {
      name: "draft_event",
      description:
        "Prepare an event for the user to review, sign, and publish. Do NOT publish directly — this creates a draft the user must confirm. Provide the kind, content, and tags. Use lookup_kind first if unsure about the schema.",
      parameters: {
        type: "object",
        properties: {
          kind: { type: "number", description: "The event kind to create." },
          content: { type: "string", description: "The content field (may be empty for tag-only events)." },
          tags: {
            type: "array",
            items: { type: "array", items: { type: "string" } },
            description: "Array of tag arrays, e.g. [[\"t\",\"nostr\"],[\"e\",\"<id>\"]].",
          },
        },
        required: ["kind", "content"],
      },
    },
  },
];

function asStringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === "string");
  return out.length ? out : undefined;
}

function toHexPubkey(identifier: string): string {
  if (/^[0-9a-f]{64}$/i.test(identifier)) return identifier.toLowerCase();
  const decoded = nip19.decode(identifier);
  if (decoded.type === "npub") return decoded.data;
  if (decoded.type === "nprofile") return decoded.data.pubkey;
  throw new Error(`Not a profile identifier: ${identifier}`);
}

function summarizeEvent(e: NostrEvent): string {
  const info = getKindInfo(e.kind);
  const tagSummary = e.tags
    .slice(0, 8)
    .map((t) => `[${t.slice(0, 2).join(": ")}]`)
    .join(" ");
  const contentPreview =
    e.content.length > 200 ? e.content.slice(0, 200) + "…" : e.content;
  return `kind ${e.kind} (${info.name}) by ${e.pubkey.slice(0, 12)}… at ${new Date(
    e.created_at * 1000
  ).toISOString()}\ntags: ${tagSummary}\ncontent: ${JSON.stringify(contentPreview)}`;
}

export async function runTool(
  name: string,
  args: Record<string, unknown>,
  ctx: ToolContext
): Promise<ToolResult> {
  const signal = ctx.signal ?? AbortSignal.timeout(8000);

  switch (name) {
    case "lookup_kind": {
      const kind = Number(args.kind);
      if (!Number.isFinite(kind)) return { content: "Invalid kind." };
      const info = getKindInfo(kind);
      return {
        content: JSON.stringify(info, null, 2),
      };
    }

    case "list_kinds": {
      const list = listKnownKinds().map((k) => ({
        kind: k.kind,
        name: k.name,
        storage: k.storage,
        nip: k.nip,
        description: k.description,
      }));
      return { content: JSON.stringify(list, null, 2) };
    }

    case "decode_nip19": {
      const id = String(args.identifier ?? "");
      try {
        const decoded = nip19.decode(id);
        return { content: JSON.stringify({ type: decoded.type, data: decoded.data }, null, 2) };
      } catch (e) {
        return { content: `Could not decode "${id}": ${(e as Error).message}` };
      }
    }

    case "whoami": {
      if (!ctx.userPubkey) {
        return { content: "Nobody is logged in. The user can sign in via the Join button." };
      }
      return {
        content: JSON.stringify({
          pubkey: ctx.userPubkey,
          npub: nip19.npubEncode(ctx.userPubkey),
        }),
      };
    }

    case "query_events": {
      const filter: NostrFilter = {};
      const kinds = (Array.isArray(args.kinds) ? args.kinds : [])
        .map(Number)
        .filter((n) => Number.isFinite(n));
      if (kinds.length) filter.kinds = kinds;
      if (asStringArray(args.authors)) filter.authors = asStringArray(args.authors);
      if (asStringArray(args.ids)) filter.ids = asStringArray(args.ids);
      // Accept both the schema-safe names (e_tags) and raw #-prefixed keys.
      const eTags = asStringArray(args.e_tags) ?? asStringArray(args["#e"]);
      const pTags = asStringArray(args.p_tags) ?? asStringArray(args["#p"]);
      const tTags = asStringArray(args.t_tags) ?? asStringArray(args["#t"]);
      const dTags = asStringArray(args.d_tags) ?? asStringArray(args["#d"]);
      if (eTags) filter["#e"] = eTags;
      if (pTags) filter["#p"] = pTags;
      if (tTags) filter["#t"] = tTags;
      if (dTags) filter["#d"] = dTags;
      if (typeof args.search === "string" && args.search) filter.search = args.search;
      if (Number.isFinite(Number(args.since))) filter.since = Number(args.since);
      if (Number.isFinite(Number(args.until))) filter.until = Number(args.until);
      const limit = Math.min(Math.max(Number(args.limit) || 20, 1), 100);
      filter.limit = limit;

      const events = await ctx.nostr.query([filter], { signal });
      events.sort((a, b) => b.created_at - a.created_at);
      const trimmed = events.slice(0, limit);

      if (!trimmed.length) {
        return { content: "No events matched that filter." };
      }
      const summary = trimmed.map(summarizeEvent).join("\n\n");
      return {
        content: `Found ${trimmed.length} event(s). They are now displayed to the user.\n\n${summary}`,
        events: trimmed,
      };
    }

    case "fetch_event": {
      const id = String(args.identifier ?? "");
      let filter: NostrFilter;
      try {
        if (/^[0-9a-f]{64}$/i.test(id)) {
          filter = { ids: [id.toLowerCase()] };
        } else {
          const decoded = nip19.decode(id);
          if (decoded.type === "note") {
            filter = { ids: [decoded.data] };
          } else if (decoded.type === "nevent") {
            filter = { ids: [decoded.data.id] };
          } else if (decoded.type === "naddr") {
            filter = {
              kinds: [decoded.data.kind],
              authors: [decoded.data.pubkey],
              "#d": [decoded.data.identifier],
            };
          } else {
            return { content: `"${id}" is a ${decoded.type}, not an event. Use fetch_profile for profiles.` };
          }
        }
      } catch (e) {
        return { content: `Could not parse "${id}": ${(e as Error).message}` };
      }
      const events = await ctx.nostr.query([{ ...filter, limit: 1 }], { signal });
      if (!events.length) return { content: "Event not found on connected relays." };
      const e = events[0];
      return { content: `Fetched event:\n${summarizeEvent(e)}`, events: [e] };
    }

    case "fetch_profile": {
      const id = String(args.identifier ?? "");
      let pubkey: string;
      try {
        pubkey = toHexPubkey(id);
      } catch (e) {
        return { content: `Could not resolve "${id}" to a pubkey: ${(e as Error).message}` };
      }
      const events = await ctx.nostr.query([{ kinds: [0], authors: [pubkey], limit: 1 }], { signal });
      if (!events.length) {
        return {
          content: `No profile (kind 0) found for ${nip19.npubEncode(pubkey)}.`,
        };
      }
      return { content: `Fetched profile:\n${summarizeEvent(events[0])}`, events: [events[0]] };
    }

    case "draft_event": {
      const kind = Number(args.kind);
      if (!Number.isFinite(kind)) return { content: "Invalid kind for draft." };
      const content = typeof args.content === "string" ? args.content : "";
      const rawTags = Array.isArray(args.tags) ? args.tags : [];
      const tags: string[][] = rawTags
        .filter((t): t is unknown[] => Array.isArray(t))
        .map((t) => t.map((x) => String(x)));
      const info = getKindInfo(kind);
      return {
        content: `Prepared a draft of kind ${kind} (${info.name}, ${storageClass(
          kind
        )}). The user must review and confirm before it is signed and published.`,
        draft: { kind, content, tags },
      };
    }

    default:
      return { content: `Unknown tool: ${name}` };
  }
}
